import { isProduction } from '@epi-selectors/environment';
import createReducer from '../helpers/CreateReducer';
import { OverlaysConstants } from '../constants/actions';

interface Notification {
  uid: string | number;
  level?: string;
  [key: string]: any;
}

interface ErrorPopup {
  display: boolean;
  closable: boolean;
  error?: any;
  showDetails?: boolean;
}

interface PasswordPopup {
  message: string;
  show: boolean;
  isLoading: boolean;
}

interface LoginPopup {
  show: boolean;
  isLoginPopup: boolean;
}

interface OverlaysState {
  notifications: Notification[];
  errorPopup: ErrorPopup;
  passwordPopup: PasswordPopup;
  loginPopup: LoginPopup;
  errorMessage?: string;
}

export const getInitialState = (): OverlaysState => ({
  notifications: [],
  errorPopup: {
    display: false,
    closable: true
  },
  passwordPopup: {
    message: '',
    show: false,
    isLoading: false
  },
  loginPopup: {
    show: false,
    isLoginPopup: true
  }
});

export default createReducer(getInitialState, {
  [`${OverlaysConstants.SHOW_NOTIFICATION}`]: (
    state: OverlaysState,
    payload: { type: string; uid: string | number; [key: string]: any }
  ) => {
    // eslint-disable-next-line no-unused-vars
    const { type, ...options } = payload;
    return { notifications: [...state.notifications, { ...options }] };
  },

  [`${OverlaysConstants.HIDE_NOTIFICATION}`]: (
    state: OverlaysState,
    payload: { uid: string | number }
  ) => {
    const notifications = state.notifications.filter(
      notification => notification.uid !== payload.uid
    );
    return { notifications };
  },

  [`${OverlaysConstants.REMOVE_ALL_NOTIFICATIONS}`]: () => ({
    notifications: []
  }),

  [`${OverlaysConstants.SHOW_ERROR_NOTIFICATION}`]: (
    state: OverlaysState,
    payload: { level?: string; [key: string]: any }
  ) => ({
    notifications: [
      ...state.notifications,
      { level: 'error', uid: Date.now(), ...payload }
    ]
  }),

  [`${OverlaysConstants.SHOW_ERROR_POPUP}`]: (
    state: OverlaysState,
    payload: { closable?: boolean; error?: any }
  ) => ({
    errorPopup: {
      display: true,
      closable: !(
        !state.errorPopup.closable ||
        (!payload.closable && payload.closable !== undefined)
      ),
      error: payload.error,
      showDetails: payload.error && !isProduction()
    }
  }),

  [`${OverlaysConstants.SET_PASSWORD_POPUP_VISIBILITY}`]: (
    state: OverlaysState,
    { show, message }: { show: boolean; message: string }
  ) => ({
    passwordPopup: { ...state.passwordPopup, show, message }
  }),

  [`${OverlaysConstants.SET_PASSWORD_POPUP_LOADING_STATE}`]: (
    state: OverlaysState,
    { isLoading }: { isLoading: boolean }
  ) => ({
    passwordPopup: { ...state.passwordPopup, isLoading }
  }),

  [`${OverlaysConstants.SET_ERROR_MESSAGE_ON_PASSWORD_POPUP}`]: (
    state: OverlaysState,
    { message }: { message: string }
  ) => ({
    errorMessage: message
  }),

  [`${OverlaysConstants.SET_ERROR_MESSAGE_OFF_PASSWORD_POPUP}`]: () => ({
    errorMessage: ''
  }),

  [`${OverlaysConstants.SET_LOGIN_POPUP_VISIBILITY}`]: (
    state: OverlaysState,
    { show }: { show: boolean }
  ) => ({
    loginPopup: {
      show,
      isLoginPopup: show ? true : state.loginPopup.isLoginPopup
    }
  }),

  [`${OverlaysConstants.CHANGE_LOGIN_POPUP_CONTENT}`]: (
    state: OverlaysState,
    { isLoginPopup }: { isLoginPopup: boolean }
  ) => ({
    loginPopup: { show: true, isLoginPopup }
  })
});
